// src/components/FilterControls.js
// Apollo Design System - Filter Controls
// Matches the Pallium Apollo reference designs
import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { RiInformationLine, RiArrowDownSLine, RiSubtractLine, RiAddLine, RiCalendarLine, RiArrowLeftSLine, RiArrowRightSLine, RiCheckLine } from 'react-icons/ri';
import { DRI_EXPLANATION } from '../constants';
import apollo from '../styles/apolloTheme';

// LocalStorage keys for filter persistence
const FILTER_STORAGE_KEY = 'cortex-filters';

// Apollo-style dropdown animation and component styles (injected once)
const apolloFilterStyles = `
/* Dropdown animations matching Apollo PalliumDropdown */
@keyframes palliumDropdownEnter {
    from {
        opacity: 0;
        transform: translateY(-8px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes palliumDropdownExit {
    from {
        opacity: 1;
        transform: translateY(0);
    }
    to {
        opacity: 0;
        transform: translateY(-8px);
    }
}

.pallium-dropdown-menu {
    animation: palliumDropdownEnter 0.15s ease-out forwards;
}

.pallium-dropdown-menu.closing {
    animation: palliumDropdownExit 0.1s ease-in forwards;
}

/* Focus states matching Apollo design system */
.pallium-filter-btn:focus-visible {
    outline: none;
    box-shadow: var(--pallium-focus-ring-primary);
}

.pallium-filter-btn:focus:not(:focus-visible) {
    outline: none;
}

/* Apollo PalliumCheckbox-style checkbox */
.pallium-checkbox-custom {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border: 2px solid var(--pallium-border-default);
    border-radius: 3px;
    background-color: var(--pallium-white);
    transition: all var(--pallium-transition-normal);
    flex-shrink: 0;
    cursor: pointer;
}

.pallium-checkbox-custom:hover {
    border-color: var(--pallium-icon-gray);
}

.pallium-checkbox-custom.checked {
    background-color: var(--pallium-success-green);
    border-color: var(--pallium-success-green);
}

/* Apollo PalliumChip-style type selector */
.pallium-type-selector {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 3px;
    background-color: var(--pallium-bg-secondary);
    border: 1px solid var(--pallium-border-default);
    border-radius: var(--pallium-border-radius-md);
}

.pallium-type-btn {
    padding: 6px 14px;
    font-size: 13px;
    font-weight: 500;
    border: none;
    border-radius: var(--pallium-border-radius);
    cursor: pointer;
    transition: all var(--pallium-transition-fast);
}

.pallium-type-btn:not(.active) {
    background-color: transparent;
    color: var(--pallium-text-secondary);
}

.pallium-type-btn:not(.active):hover {
    background-color: var(--pallium-white);
    color: var(--pallium-text-primary);
}

.pallium-type-btn.active {
    background-color: var(--pallium-primary-purple);
    color: var(--pallium-white);
    box-shadow: var(--pallium-shadow-sm);
}

/* Apollo PalliumChip-style filter chips */
.pallium-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.25;
    border-radius: 9999px;
    border: 1px solid var(--pallium-stroke-light);
    background-color: var(--pallium-white);
    color: var(--pallium-text-primary);
    cursor: pointer;
    transition: all var(--pallium-transition-normal);
    white-space: nowrap;
}

.pallium-chip:hover:not(.disabled) {
    background-color: var(--pallium-purple-1);
    border-color: var(--pallium-primary-purple);
}

.pallium-chip:focus-visible {
    outline: none;
    box-shadow: var(--pallium-focus-ring-primary);
}

.pallium-chip.selected {
    background-color: var(--pallium-primary-purple);
    border-color: var(--pallium-primary-purple);
    color: var(--pallium-white);
}

.pallium-chip.selected:hover:not(.disabled) {
    background-color: var(--pallium-purple-6);
    border-color: var(--pallium-purple-6);
}

.pallium-chip.disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* Apollo PalliumDropdown-style option hover */
.pallium-dropdown-option {
    padding: 12px 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    color: var(--pallium-text-primary);
    transition: background-color var(--pallium-transition-fast);
}

.pallium-dropdown-option:hover {
    background-color: var(--pallium-bg-secondary);
}

.pallium-dropdown-option.selected {
    background-color: var(--pallium-selected-option-bg);
    color: var(--pallium-primary-purple);
    font-weight: 500;
}
`;

// Inject Apollo filter styles once
if (typeof document !== 'undefined' && !document.getElementById('pallium-filter-styles')) {
    const styleEl = document.createElement('style');
    styleEl.id = 'pallium-filter-styles';
    styleEl.textContent = apolloFilterStyles;
    document.head.appendChild(styleEl);
}

// Hook for click-outside detection
const useClickOutside = (ref, callback) => {
    useEffect(() => {
        const handleClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                callback();
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [ref, callback]);
};

// Base button styles following Apollo PalliumButton/PalliumDropdown pattern
const getButtonBaseStyle = (isActive, hasSelection, disabled) => {
    const baseStyle = {
        transition: apollo.transition.fast,
        borderRadius: apollo.radius.default,
        fontWeight: 500,
        letterSpacing: '0.01em',
    };
    
    if (disabled) {
        return {
            ...baseStyle,
            opacity: 0.6,
            cursor: 'not-allowed',
            backgroundColor: apollo.color.bgSecondary,
            borderColor: apollo.color.borderSubtle,
            color: apollo.color.textDisabled,
        };
    }
    if (isActive || hasSelection) {
        return {
            ...baseStyle,
            backgroundColor: apollo.color.selectedBg,
            borderColor: apollo.color.primary,
            color: apollo.color.primary,
            boxShadow: apollo.shadow.xs,
        };
    }
    return {
        ...baseStyle,
        backgroundColor: apollo.color.bgPrimary,
        borderColor: apollo.color.borderDefault,
        color: apollo.color.textPrimary,
    };
};

// Dropdown menu base styles matching Apollo PalliumDropdown
const getDropdownMenuStyle = () => ({
    backgroundColor: apollo.color.bgPrimary,
    border: `1px solid ${apollo.color.borderDefault}`,
    borderRadius: apollo.radius.md,
    boxShadow: apollo.shadow.md,
    zIndex: apollo.z.dropdown,
    marginTop: '4px',
});

// Multi-select dropdown with checkboxes - Apollo PalliumDropdown + PalliumCheckbox style
export const DropdownCheckbox = ({ options, selectedOptions, onChange, title, disabled = false, showClearButton = true, disabledTooltip = null, displayFormatter = null }) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef(null);
    
    useClickOutside(ref, () => setIsOpen(false));
    
    const hasSelections = selectedOptions.length > 0;
    
    const handleClearAll = (e) => {
        e.stopPropagation();
        selectedOptions.forEach(opt => onChange(opt));
    };

    const buttonElement = (
        <button
            type="button"
            className="pallium-filter-btn inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border"
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            style={getButtonBaseStyle(false, hasSelections, disabled)}
            onMouseOver={(e) => {
                if (!disabled && !hasSelections) {
                    e.currentTarget.style.borderColor = apollo.color.primary;
                }
            }}
            onMouseOut={(e) => {
                if (!disabled && !hasSelections) {
                    e.currentTarget.style.borderColor = apollo.color.borderDefault;
                }
            }}
        >
            {title}
            {hasSelections && !disabled && (
                <span 
                    className="inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white"
                    style={{ 
                        backgroundColor: apollo.color.primary, 
                        borderRadius: '50%',
                    }}
                >
                    {selectedOptions.length}
                </span>
            )}
            <RiArrowDownSLine 
                className="w-4 h-4" 
                style={{ 
                    transition: apollo.transition.normal,
                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    color: apollo.color.iconGray,
                }} 
            />
        </button>
    );

    return (
        <div className="relative" ref={ref}>
            {disabled && disabledTooltip ? (
                <Tooltip content={disabledTooltip}>
                    {buttonElement}
                </Tooltip>
            ) : buttonElement}

            {isOpen && !disabled && (
                <div 
                    className="pallium-dropdown-menu absolute left-0 w-64"
                    style={getDropdownMenuStyle()}
                >
                    {hasSelections && showClearButton && (
                        <div 
                            className="px-4 py-2.5 flex justify-between items-center"
                            style={{ borderBottom: `1px solid ${apollo.color.borderSubtle}` }}
                        >
                            <span className="text-xs font-medium" style={{ color: apollo.color.textSecondary }}>{selectedOptions.length} selected</span>
                            <button 
                                onClick={handleClearAll}
                                className="text-xs font-medium"
                                style={{ color: apollo.color.linkDefault, transition: apollo.transition.fast }}
                                onMouseOver={(e) => e.currentTarget.style.color = apollo.color.linkHover}
                                onMouseOut={(e) => e.currentTarget.style.color = apollo.color.linkDefault}
                            >
                                Clear all
                            </button>
                        </div>
                    )}
                    <div className="max-h-52 overflow-y-auto py-1">
                        {options.length === 0 ? (
                            <div className="px-3 py-2 text-sm italic text-center" style={{ color: apollo.color.textSecondary }}>No options available</div>
                        ) : (
                            options.map(option => {
                                const isChecked = selectedOptions.includes(option);
                                return (
                                    <div 
                                        key={option} 
                                        className="flex items-center px-3 py-2 cursor-pointer"
                                        style={{ 
                                            color: apollo.color.textPrimary,
                                            transition: apollo.transition.fast
                                        }}
                                        onClick={() => onChange(option)}
                                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = apollo.color.bgSecondary}
                                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                        <div 
                                            className={`pallium-checkbox-custom ${isChecked ? 'checked' : ''}`}
                                            style={{
                                                backgroundColor: isChecked ? apollo.color.success : apollo.color.white,
                                                borderColor: isChecked ? apollo.color.success : apollo.color.borderDefault,
                                            }}
                                        >
                                            {isChecked && <RiCheckLine style={{ width: 10, height: 10, color: 'white' }} />}
                                        </div>
                                        <span className="ml-2.5 text-sm truncate" style={{ lineHeight: 1.5 }}>{displayFormatter ? displayFormatter(option) : option}</span>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Apollo-style tooltip - positioned BELOW to avoid cutoff at top
export const Tooltip = ({ content, children }) => {
    const [isVisible, setIsVisible] = useState(false);
    const ref = useRef(null);
    
    return (
        <div className="relative inline-flex items-center" ref={ref}>
            <div
                className="inline-flex items-center cursor-help"
                onMouseEnter={() => setIsVisible(true)}
                onMouseLeave={() => setIsVisible(false)}
            >
                {children}
            </div>
            {isVisible && (
                <div 
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 p-4 text-sm leading-relaxed"
                    style={{ 
                        backgroundColor: apollo.color.darkNavy,
                        color: apollo.color.white,
                        borderRadius: apollo.radius.lg,
                        boxShadow: apollo.shadow.lg,
                        zIndex: apollo.z.dropdown,
                    }}
                >
                    {content}
                    <div 
                        className="absolute bottom-full left-1/2 -translate-x-1/2 w-0 h-0"
                        style={{
                            borderLeft: '8px solid transparent',
                            borderRight: '8px solid transparent',
                            borderBottom: `8px solid ${apollo.color.darkNavy}`
                        }}
                    />
                </div>
            )}
        </div>
    );
};

// Single-select dropdown - Apollo PalliumDropdown style
export const SingleSelectDropdown = ({ 
    id, 
    label, 
    value, 
    onChange, 
    options, 
    allLabel = "All", 
    disabled = false,
    loading = false,
    capitalize = true,
    disabledTooltip = null,
    displayFormatter = null  // Optional function to transform display of options (e.g., email to name)
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef(null);
    
    useClickOutside(ref, () => setIsOpen(false));
    
    // Format value for display - use displayFormatter if provided
    const formatForDisplay = (val) => {
        if (!val) return allLabel;
        const formatted = displayFormatter ? displayFormatter(val) : val;
        return capitalize ? formatted.charAt(0).toUpperCase() + formatted.slice(1) : formatted;
    };
    
    const hasSelection = value && value !== '';
    const displayValue = hasSelection ? formatForDisplay(value) : allLabel;

    const buttonElement = (
        <button
            id={id}
            type="button"
            onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
            disabled={disabled || loading}
            className="pallium-filter-btn inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border"
            style={getButtonBaseStyle(false, hasSelection, disabled)}
            onMouseOver={(e) => {
                if (!disabled && !hasSelection) {
                    e.currentTarget.style.borderColor = apollo.color.primary;
                }
            }}
            onMouseOut={(e) => {
                if (!disabled && !hasSelection) {
                    e.currentTarget.style.borderColor = apollo.color.borderDefault;
                }
            }}
        >
            <span style={{ color: apollo.color.textSecondary, fontWeight: 400 }}>{label}:</span>
            <span style={{ fontWeight: hasSelection && !disabled ? 600 : 500 }}>{displayValue}</span>
            {loading ? (
                <span 
                    className="w-3.5 h-3.5 border-2 rounded-full animate-spin" 
                    style={{ borderColor: apollo.color.borderDefault, borderTopColor: apollo.color.primary }} 
                />
            ) : (
                <RiArrowDownSLine 
                    className="w-4 h-4" 
                    style={{ 
                        transition: apollo.transition.normal,
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        color: apollo.color.iconGray,
                    }} 
                />
            )}
        </button>
    );

    return (
        <div className="relative" ref={ref}>
            {disabled && disabledTooltip ? (
                <Tooltip content={disabledTooltip}>
                    {buttonElement}
                </Tooltip>
            ) : buttonElement}
            
            {isOpen && !disabled && (
                <div 
                    className="pallium-dropdown-menu absolute left-0 min-w-[160px] py-1"
                    style={getDropdownMenuStyle()}
                >
                    <button
                        onClick={() => { onChange(''); setIsOpen(false); }}
                        className="w-full text-left px-4 py-2.5 text-sm"
                        style={{
                            backgroundColor: !hasSelection ? apollo.color.selectedBg : 'transparent',
                            color: !hasSelection ? apollo.color.primary : apollo.color.textPrimary,
                            fontWeight: !hasSelection ? 500 : 400,
                            transition: apollo.transition.fast
                        }}
                        onMouseOver={(e) => { if (hasSelection) e.currentTarget.style.backgroundColor = apollo.color.bgSecondary; }}
                        onMouseOut={(e) => { if (hasSelection) e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                        {allLabel}
                    </button>
                    {options.map(opt => (
                        <button
                            key={opt}
                            onClick={() => { onChange(opt); setIsOpen(false); }}
                            className="w-full text-left px-4 py-2.5 text-sm"
                            style={{
                                backgroundColor: value === opt ? apollo.color.selectedBg : 'transparent',
                                color: value === opt ? apollo.color.primary : apollo.color.textPrimary,
                                fontWeight: value === opt ? 500 : 400,
                                transition: apollo.transition.fast
                            }}
                            onMouseOver={(e) => { if (value !== opt) e.currentTarget.style.backgroundColor = apollo.color.bgSecondary; }}
                            onMouseOut={(e) => { if (value !== opt) e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                            {formatForDisplay(opt)}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// DRI Filter with tooltip - Apollo style
export const DRIFilter = ({ 
    dris, 
    selectedDris, 
    onDriChange, 
    loading = false,
    displayFormatter = null  // Optional function to transform display of DRIs (e.g., email to name)
}) => {
    return (
        <div className="flex items-center gap-0.5">
            <DropdownCheckbox
                options={dris}
                selectedOptions={selectedDris}
                onChange={onDriChange}
                title="DRI"
                disabled={loading}
                displayFormatter={displayFormatter}
            />
            <Tooltip content={DRI_EXPLANATION}>
                <RiInformationLine className="w-3 h-3" style={{ color: apollo.color.iconGray }} />
            </Tooltip>
        </div>
    );
};

// Stepper filter for numeric values - Apollo inline control style
export const StepperFilter = ({ 
    value, 
    onChange, 
    min = 0, 
    max = 10, 
    step = 1, 
    label,
    tooltip,
    disabled = false,
    disabledTooltip = null
}) => {
    const handleDecrement = () => {
        if (disabled) return;
        const newValue = Math.max(min, value - step);
        onChange(newValue);
    };
    
    const handleIncrement = () => {
        if (disabled) return;
        const newValue = Math.min(max, value + step);
        onChange(newValue);
    };
    
    const displayValue = step < 1 ? value.toFixed(1) : value;
    const isActive = value > min && !disabled;
    
    const content = (
        <div className={`flex items-center gap-2 ${disabled ? 'opacity-60' : ''}`}>
            <span className="text-sm font-medium" style={{ color: disabled ? apollo.color.textDisabled : apollo.color.textSecondary }}>{label}</span>
            {tooltip && !disabled && (
                <Tooltip content={tooltip}>
                    <RiInformationLine className="w-3.5 h-3.5" style={{ color: apollo.color.iconGray }} />
                </Tooltip>
            )}
            <div 
                className="flex items-center overflow-hidden"
                style={{
                    border: `1px solid ${!disabled && isActive ? apollo.color.primary : apollo.color.borderDefault}`,
                    backgroundColor: !disabled && isActive ? apollo.color.selectedBg : disabled ? apollo.color.bgSecondary : apollo.color.bgPrimary,
                    borderRadius: apollo.radius.default,
                    transition: apollo.transition.fast,
                    boxShadow: !disabled && isActive ? apollo.shadow.xs : 'none',
                }}
            >
                <button
                    onClick={handleDecrement}
                    disabled={value <= min || disabled}
                    className="pallium-filter-btn px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ 
                        cursor: disabled || value <= min ? 'not-allowed' : 'pointer',
                        transition: apollo.transition.fast,
                        border: 'none',
                        background: 'transparent'
                    }}
                    onMouseOver={(e) => { if (!disabled && value > min) e.currentTarget.style.backgroundColor = apollo.color.bgSecondary; }}
                    onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                    <RiSubtractLine className="h-3.5 w-3.5" style={{ color: disabled ? apollo.color.textDisabled : apollo.color.textSecondary }} />
                </button>
                <span 
                    className="px-2 py-1 font-semibold text-sm min-w-[28px] text-center"
                    style={{ color: !disabled && isActive ? apollo.color.primary : disabled ? apollo.color.textDisabled : apollo.color.textPrimary }}
                >
                    {displayValue}
                </span>
                <button
                    onClick={handleIncrement}
                    disabled={value >= max || disabled}
                    className="pallium-filter-btn px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ 
                        cursor: disabled || value >= max ? 'not-allowed' : 'pointer',
                        transition: apollo.transition.fast,
                        border: 'none',
                        background: 'transparent'
                    }}
                    onMouseOver={(e) => { if (!disabled && value < max) e.currentTarget.style.backgroundColor = apollo.color.bgSecondary; }}
                    onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                    <RiAddLine className="h-3.5 w-3.5" style={{ color: disabled ? apollo.color.textDisabled : apollo.color.textSecondary }} />
                </button>
            </div>
        </div>
    );
    
    if (disabled && disabledTooltip) {
        return (
            <Tooltip content={disabledTooltip}>
                {content}
            </Tooltip>
        );
    }
    
    return content;
};

// Department selector - Apollo PalliumDropdown style
export const DepartmentSelector = ({
    departments,
    selectedDepartment,
    onDepartmentChange,
    loading = false,
    disabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef(null);
    
    useClickOutside(ref, () => setIsOpen(false));

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
                disabled={disabled || loading}
                className="pallium-filter-btn inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border"
                style={getButtonBaseStyle(false, false, disabled || loading)}
                onMouseOver={(e) => {
                    if (!disabled && !loading) {
                        e.currentTarget.style.borderColor = apollo.color.primary;
                    }
                }}
                onMouseOut={(e) => {
                    if (!disabled && !loading) {
                        e.currentTarget.style.borderColor = apollo.color.borderDefault;
                    }
                }}
            >
                <span style={{ color: apollo.color.textSecondary, fontWeight: 400 }}>Department:</span>
                <span style={{ fontWeight: 600 }}>{selectedDepartment || 'Select'}</span>
                {loading ? (
                    <span className="w-3.5 h-3.5 border-2 rounded-full animate-spin" style={{ borderColor: apollo.color.borderDefault, borderTopColor: apollo.color.primary }} />
                ) : (
                    <RiArrowDownSLine 
                        className="w-4 h-4" 
                        style={{ 
                            transition: apollo.transition.normal,
                            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            color: apollo.color.iconGray,
                        }} 
                    />
                )}
            </button>
            
            {isOpen && (
                <div 
                    className="pallium-dropdown-menu absolute left-0 min-w-[180px] max-h-64 overflow-y-auto py-1"
                    style={getDropdownMenuStyle()}
                >
                    {departments.map(dept => (
                        <button
                            key={dept}
                            onClick={() => { onDepartmentChange(dept); setIsOpen(false); }}
                            className="w-full text-left px-4 py-2.5 text-sm"
                            style={{
                                backgroundColor: selectedDepartment === dept ? apollo.color.selectedBg : 'transparent',
                                color: selectedDepartment === dept ? apollo.color.primary : apollo.color.textPrimary,
                                fontWeight: selectedDepartment === dept ? 500 : 400,
                                transition: apollo.transition.fast
                            }}
                            onMouseOver={(e) => { if (selectedDepartment !== dept) e.currentTarget.style.backgroundColor = apollo.color.bgSecondary; }}
                            onMouseOut={(e) => { if (selectedDepartment !== dept) e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                            {dept}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// Date Range Filter - Apollo calendar/date picker style
export const DateRangeFilter = ({ startDate, endDate, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [tempStartDate, setTempStartDate] = useState(startDate);
    const [tempEndDate, setTempEndDate] = useState(endDate);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selecting, setSelecting] = useState('start');
    const [selectedPreset, setSelectedPreset] = useState(null);
    const dropdownRef = useRef(null);

    const presets = [
        { label: 'Last 7 days', days: 7 },
        { label: 'Last 30 days', days: 30 },
        { label: 'Last 90 days', days: 90 },
        { label: 'Last 6 months', days: 180 },
        { label: 'Last 9 months', days: 270 },
        { label: 'Last 12 months', days: 365 }
    ];

    useEffect(() => {
        setTempStartDate(startDate);
        setTempEndDate(endDate);
    }, [startDate, endDate]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const formatDisplayDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString + 'T00:00:00');
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const handlePresetClick = (days) => {
        const end = new Date().toISOString().split('T')[0];
        const start = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        setTempStartDate(start);
        setTempEndDate(end);
        setSelectedPreset(days);
    };

    const handleApply = () => {
        onChange({ start: tempStartDate, end: tempEndDate });
        setIsOpen(false);
    };

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        return { daysInMonth: lastDay.getDate(), startingDayOfWeek: firstDay.getDay(), year, month };
    };

    const handleDateClick = (day) => {
        const { year, month } = getDaysInMonth(currentMonth);
        const selectedDate = new Date(year, month, day).toISOString().split('T')[0];
        setSelectedPreset(null);
        if (selecting === 'start') {
            setTempStartDate(selectedDate);
            if (tempEndDate && selectedDate > tempEndDate) setTempEndDate(selectedDate);
            setSelecting('end');
        } else {
            if (tempStartDate && selectedDate < tempStartDate) {
                setTempStartDate(selectedDate);
                setTempEndDate(tempStartDate);
            } else {
                setTempEndDate(selectedDate);
            }
            setSelecting('start');
        }
    };

    const isDateInRange = (day) => {
        if (!tempStartDate || !tempEndDate) return false;
        const { year, month } = getDaysInMonth(currentMonth);
        const date = new Date(year, month, day).toISOString().split('T')[0];
        return date >= tempStartDate && date <= tempEndDate;
    };

    const isDateSelected = (day) => {
        const { year, month } = getDaysInMonth(currentMonth);
        const date = new Date(year, month, day).toISOString().split('T')[0];
        return date === tempStartDate || date === tempEndDate;
    };

    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                data-testid="date-range-picker-button"
                onClick={() => setIsOpen(!isOpen)}
                className="pallium-filter-btn inline-flex items-center gap-2 px-3 py-1.5 text-sm border"
                style={getButtonBaseStyle(false, false, false)}
                onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = apollo.color.primary;
                }}
                onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = apollo.color.borderDefault;
                }}
            >
                <RiCalendarLine className="h-4 w-4" style={{ color: apollo.color.primary }} />
                <span style={{ fontWeight: 500 }}>{formatDisplayDate(startDate)} - {formatDisplayDate(endDate)}</span>
                <RiArrowDownSLine 
                    className="w-4 h-4" 
                    style={{ 
                        transition: apollo.transition.normal,
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        color: apollo.color.iconGray,
                    }} 
                />
            </button>

            {isOpen && (
                <div 
                    className="pallium-dropdown-menu absolute left-0 w-[340px]"
                    style={getDropdownMenuStyle()}
                >
                    <div className="flex">
                        <div 
                            className="w-24 p-2"
                            style={{ 
                                backgroundColor: apollo.color.bgSecondary, 
                                borderRight: `1px solid ${apollo.color.borderSubtle}`,
                                borderRadius: `${apollo.radius.default} 0 0 ${apollo.radius.default}`
                            }}
                        >
                            <div className="text-[10px] font-semibold uppercase tracking-wider mb-2 px-1.5" style={{ color: apollo.color.textSecondary }}>Quick Select</div>
                            {presets.map((preset) => {
                                const isSelected = selectedPreset === preset.days;
                                return (
                                <button
                                    key={preset.label}
                                    onClick={() => handlePresetClick(preset.days)}
                                    className="w-full text-left px-1.5 py-1 text-[11px]"
                                    style={{ 
                                        color: isSelected ? apollo.color.primary : apollo.color.textSecondary,
                                        backgroundColor: isSelected ? apollo.color.bgPrimary : 'transparent',
                                        fontWeight: isSelected ? 500 : 400,
                                        borderRadius: apollo.radius.sm,
                                        transition: apollo.transition.fast
                                    }}
                                    onMouseOver={(e) => { 
                                        if (!isSelected) {
                                            e.target.style.color = apollo.color.primary;
                                            e.target.style.backgroundColor = apollo.color.bgPrimary;
                                        }
                                    }}
                                    onMouseOut={(e) => { 
                                        if (!isSelected) {
                                            e.target.style.color = apollo.color.textSecondary;
                                            e.target.style.backgroundColor = 'transparent';
                                        }
                                    }}
                                >
                                    {preset.label}
                                </button>
                                );
                            })}
                        </div>
                        <div className="flex-1 p-2.5">
                            <div className="flex items-center justify-between mb-2">
                                <button 
                                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))} 
                                    className="pallium-filter-btn p-0.5"
                                    style={{ 
                                        borderRadius: apollo.radius.sm,
                                        transition: apollo.transition.fast,
                                        border: 'none',
                                        background: 'transparent'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = apollo.color.bgSecondary}
                                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                    <RiArrowLeftSLine className="h-4 w-4" style={{ color: apollo.color.textSecondary }} />
                                </button>
                                <div className="text-xs font-semibold" style={{ color: apollo.color.textPrimary }}>
                                    {currentMonth.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                                </div>
                                <button 
                                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))} 
                                    className="pallium-filter-btn p-0.5"
                                    style={{ 
                                        borderRadius: apollo.radius.sm,
                                        transition: apollo.transition.fast,
                                        border: 'none',
                                        background: 'transparent'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = apollo.color.bgSecondary}
                                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                    <RiArrowRightSLine className="h-4 w-4" style={{ color: apollo.color.textSecondary }} />
                                </button>
                            </div>
                            <div className="grid grid-cols-7 gap-0.5 mb-0.5">
                                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                                    <div key={i} className="h-5 w-5 flex items-center justify-center text-[10px]" style={{ color: apollo.color.textSecondary }}>{d}</div>
                                ))}
                            </div>
                            <div className="grid grid-cols-7 gap-0.5">
                                {Array.from({ length: startingDayOfWeek }).map((_, i) => <div key={`e-${i}`} className="h-5 w-5" />)}
                                {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => (
                                    <button
                                        key={day}
                                        onClick={() => handleDateClick(day)}
                                        className="h-5 w-5 flex items-center justify-center text-[10px]"
                                        style={{
                                            backgroundColor: isDateSelected(day) ? apollo.color.primary : isDateInRange(day) ? apollo.color.purple1 : 'transparent',
                                            color: isDateSelected(day) ? apollo.color.white : apollo.color.textPrimary,
                                            fontWeight: isDateSelected(day) ? 600 : 400,
                                            borderRadius: apollo.radius.sm,
                                            transition: apollo.transition.fast
                                        }}
                                        onMouseOver={(e) => {
                                            if (!isDateSelected(day) && !isDateInRange(day)) {
                                                e.currentTarget.style.backgroundColor = apollo.color.bgSecondary;
                                            }
                                        }}
                                        onMouseOut={(e) => {
                                            if (!isDateSelected(day) && !isDateInRange(day)) {
                                                e.currentTarget.style.backgroundColor = 'transparent';
                                            }
                                        }}
                                    >
                                        {day}
                                    </button>
                                ))}
                            </div>
                            <div 
                                className="flex items-center justify-between mt-2 pt-2"
                                style={{ borderTop: `1px solid ${apollo.color.borderSubtle}` }}
                            >
                                <div className="text-[10px]" style={{ color: apollo.color.textSecondary }}>
                                    {selecting === 'start' ? 'Select start' : 'Select end'}
                                </div>
                                <button
                                    onClick={handleApply}
                                    disabled={!tempStartDate || !tempEndDate}
                                    className="px-2.5 py-1 text-[10px] font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                    style={{ 
                                        backgroundColor: apollo.color.buttonPrimaryBg,
                                        borderRadius: apollo.radius.default,
                                        transition: apollo.transition.fast
                                    }}
                                    onMouseOver={(e) => { if (!e.target.disabled) e.target.style.backgroundColor = apollo.color.buttonPrimaryHover; }}
                                    onMouseOut={(e) => { if (!e.target.disabled) e.target.style.backgroundColor = apollo.color.buttonPrimaryBg; }}
                                >
                                    Apply
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Single date picker - Apollo calendar style
export const SingleDatePicker = ({ value, onChange, disabled, align = 'left' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(() => value ? new Date(value + 'T00:00:00') : new Date());
    const buttonRef = useRef(null);
    const dropdownRef = useRef(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

    useEffect(() => {
        const handleClickOutside = (event) => {
            const clickedButton = buttonRef.current && buttonRef.current.contains(event.target);
            const clickedDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);
            if (!clickedButton && !clickedDropdown) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const updatePosition = () => {
            if (!buttonRef.current) return;
            const rect = buttonRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const dropdownWidth = dropdownRef.current?.offsetWidth || 240;
            const dropdownHeight = dropdownRef.current?.offsetHeight || 320;

            let left = align === 'right' ? rect.right - dropdownWidth : rect.left;
            let top = rect.bottom + 6;

            if (left + dropdownWidth > viewportWidth - 8) {
                left = viewportWidth - dropdownWidth - 8;
            }
            if (left < 8) left = 8;

            if (top + dropdownHeight > viewportHeight - 8) {
                const aboveTop = rect.top - dropdownHeight - 6;
                if (aboveTop >= 8) {
                    top = aboveTop;
                }
            }

            setDropdownPosition({ top, left });
        };

        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);

        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen, align, currentMonth, value]);

    const formatDisplayDate = (dateString) => {
        if (!dateString) return 'No date';
        const date = new Date(dateString + 'T00:00:00');
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        return { daysInMonth: lastDay.getDate(), startingDayOfWeek: firstDay.getDay(), year, month };
    };

    const handleDateClick = (day) => {
        const { year, month } = getDaysInMonth(currentMonth);
        const selectedDate = new Date(year, month, day).toISOString().split('T')[0];
        onChange(selectedDate);
        setIsOpen(false);
    };

    const isDateSelected = (day) => {
        if (!value) return false;
        const { year, month } = getDaysInMonth(currentMonth);
        const date = new Date(year, month, day).toISOString().split('T')[0];
        return date === value;
    };

    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);

    return (
        <div className="relative inline-block">
            <button
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className="pallium-filter-btn px-2 py-1 text-sm border"
                style={getButtonBaseStyle(false, false, disabled)}
                ref={buttonRef}
            >
                {formatDisplayDate(value)}
            </button>
            
            {isOpen && typeof document !== 'undefined' && ReactDOM.createPortal(
                <div 
                    ref={dropdownRef}
                    className="pallium-dropdown-menu p-3 min-w-[240px]"
                    style={{
                        ...getDropdownMenuStyle(),
                        position: 'fixed',
                        top: dropdownPosition.top,
                        left: dropdownPosition.left,
                        marginTop: 0,
                        zIndex: 9999
                    }}
                >
                    {/* Month navigation */}
                    <div className="flex items-center justify-between mb-2">
                        <button
                            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                            className="pallium-filter-btn p-1"
                            style={{ 
                                borderRadius: apollo.radius.sm,
                                transition: apollo.transition.fast,
                                border: 'none',
                                background: 'transparent'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = apollo.color.bgSecondary}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                            <svg className="h-4 w-4" style={{ color: apollo.color.textSecondary }} viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                        </button>
                        <span className="text-sm font-medium" style={{ color: apollo.color.textPrimary }}>
                            {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </span>
                        <button
                            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                            className="pallium-filter-btn p-1"
                            style={{ 
                                borderRadius: apollo.radius.sm,
                                transition: apollo.transition.fast,
                                border: 'none',
                                background: 'transparent'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = apollo.color.bgSecondary}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                            <svg className="h-4 w-4" style={{ color: apollo.color.textSecondary }} viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </div>
                    
                    {/* Day headers */}
                    <div className="grid grid-cols-7 gap-0.5 mb-1">
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                            <div key={i} className="h-6 w-6 flex items-center justify-center text-xs" style={{ color: apollo.color.textSecondary }}>{d}</div>
                        ))}
                    </div>
                    
                    {/* Calendar grid */}
                    <div className="grid grid-cols-7 gap-0.5">
                        {Array.from({ length: startingDayOfWeek }).map((_, i) => <div key={`e-${i}`} className="h-6 w-6" />)}
                        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => (
                            <button
                                key={day}
                                onClick={() => handleDateClick(day)}
                                className="h-6 w-6 flex items-center justify-center text-xs"
                                style={{
                                    backgroundColor: isDateSelected(day) ? apollo.color.primary : 'transparent',
                                    color: isDateSelected(day) ? apollo.color.white : apollo.color.textPrimary,
                                    fontWeight: isDateSelected(day) ? 600 : 400,
                                    borderRadius: apollo.radius.sm,
                                    transition: apollo.transition.fast
                                }}
                                onMouseOver={(e) => { if (!isDateSelected(day)) e.currentTarget.style.backgroundColor = apollo.color.bgSecondary; }}
                                onMouseOut={(e) => { if (!isDateSelected(day)) e.currentTarget.style.backgroundColor = 'transparent'; }}
                            >
                                {day}
                            </button>
                        ))}
                    </div>
                    
                    {/* Clear button */}
                    {value && (
                        <div className="flex justify-end mt-2 pt-2" style={{ borderTop: `1px solid ${apollo.color.borderSubtle}` }}>
                            <button
                                onClick={() => { onChange(''); setIsOpen(false); }}
                                className="text-xs"
                                style={{ 
                                    color: apollo.color.textSecondary,
                                    transition: apollo.transition.fast
                                }}
                                onMouseOver={(e) => e.currentTarget.style.color = apollo.color.textPrimary}
                                onMouseOut={(e) => e.currentTarget.style.color = apollo.color.textSecondary}
                            >
                                Clear
                            </button>
                        </div>
                    )}
                </div>,
                document.body
            )}
        </div>
    );
};

// Helper to get persisted filters from localStorage
export const getPersistedFilters = () => {
    try {
        const stored = localStorage.getItem(FILTER_STORAGE_KEY);
        if (stored) {
            return JSON.parse(stored);
        }
    } catch (e) {
        console.error('Error reading persisted filters:', e);
    }
    return {};
};

// Helper to persist filters to localStorage
export const persistFilters = (filters) => {
    try {
        const existing = getPersistedFilters();
        const updated = { ...existing, ...filters };
        localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(updated));
    } catch (e) {
        console.error('Error persisting filters:', e);
    }
};

// Hook for using persisted filters
export const usePersistedFilters = (key, defaultValue) => {
    const [value, setValue] = useState(() => {
        const persisted = getPersistedFilters();
        return persisted[key] !== undefined ? persisted[key] : defaultValue;
    });

    const setPersistedValue = (newValue) => {
        setValue(newValue);
        persistFilters({ [key]: newValue });
    };

    return [value, setPersistedValue];
};

// Toggle button - Apollo PalliumChip style with pill shape
export const ToggleButton = ({
    label,
    isActive,
    onClick,
    disabled = false,
    disabledTooltip = null,
    activeColor = 'purple' // 'purple', 'orange', 'blue', etc.
}) => {
    // Apollo PalliumChip-inspired active styles
    const getActiveStyles = () => {
        if (activeColor === 'orange') {
            return { 
                backgroundColor: apollo.color.warning, 
                borderColor: apollo.color.warning, 
                color: apollo.color.white,
                boxShadow: apollo.shadow.xs,
            };
        }
        if (activeColor === 'blue') {
            return { 
                backgroundColor: apollo.color.info, 
                borderColor: apollo.color.info, 
                color: apollo.color.white,
                boxShadow: apollo.shadow.xs,
            };
        }
        // Default purple - matches PalliumChip selected state
        return { 
            backgroundColor: apollo.color.primary, 
            borderColor: apollo.color.primary, 
            color: apollo.color.white,
            boxShadow: apollo.shadow.xs,
        };
    };

    // Get base styles for inactive/disabled states - chip style
    const getChipBaseStyle = () => {
        if (disabled) {
            return {
                backgroundColor: apollo.color.white,
                borderColor: apollo.color.strokeLight,
                color: apollo.color.textPrimary,
                opacity: 0.5,
                cursor: 'not-allowed',
            };
        }
        return {
            backgroundColor: apollo.color.white,
            borderColor: apollo.color.strokeLight,
            color: apollo.color.textPrimary,
        };
    };
    
    const buttonElement = (
        <button
            onClick={disabled ? undefined : onClick}
            disabled={disabled}
            className={`pallium-chip ${isActive && !disabled ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
            style={{
                ...getChipBaseStyle(),
                ...(isActive && !disabled ? getActiveStyles() : {}),
                transition: apollo.transition.normal,
                borderRadius: apollo.radius.pill,
                fontWeight: 500,
                fontSize: '0.875rem',
                padding: '0.5rem 1rem',
                lineHeight: 1.25,
            }}
            onMouseOver={(e) => {
                if (!disabled && !isActive) {
                    e.currentTarget.style.backgroundColor = apollo.color.purple1;
                    e.currentTarget.style.borderColor = apollo.color.primary;
                }
                if (!disabled && isActive && activeColor === 'purple') {
                    e.currentTarget.style.backgroundColor = apollo.color.primaryDark;
                    e.currentTarget.style.borderColor = apollo.color.primaryDark;
                }
            }}
            onMouseOut={(e) => {
                if (!disabled && !isActive) {
                    e.currentTarget.style.backgroundColor = apollo.color.white;
                    e.currentTarget.style.borderColor = apollo.color.strokeLight;
                }
                if (!disabled && isActive && activeColor === 'purple') {
                    e.currentTarget.style.backgroundColor = apollo.color.primary;
                    e.currentTarget.style.borderColor = apollo.color.primary;
                }
            }}
        >
            {label}
        </button>
    );
    
    if (disabled && disabledTooltip) {
        return (
            <Tooltip content={disabledTooltip}>
                {buttonElement}
            </Tooltip>
        );
    }
    
    return buttonElement;
};

const FilterControls = {
    DropdownCheckbox,
    Tooltip,
    SingleSelectDropdown,
    DRIFilter,
    DepartmentSelector,
    StepperFilter,
    DateRangeFilter,
    ToggleButton,
    getPersistedFilters,
    persistFilters,
    usePersistedFilters
};

export default FilterControls;
